import type { HotelDestinationPayload } from '@/src/api/requests/search-hotels'
import { getFormattedDate } from '@/src/utils/date'

import type { HotelRoomConfig } from '../searchForm/hooks/useHotelSearchManager'

export type HotelDestinationKind = 'city' | 'hotel'

export interface BuildHotelSearchUrlInput {
    kind: HotelDestinationKind
    payload: HotelDestinationPayload
    destinationName: string
    checkIn: Date
    checkOut: Date
    nights: number
    roomConfigs: HotelRoomConfig[]
    locale: string
}

/**
 * Builds the `/hotel/searchHotels` query string matching the legacy Flyin hotel SRP.
 *
 * Host-relative on purpose: the SRP is served from every domain this app runs on,
 * so the link keeps the user on the host they are already browsing.
 */
export const buildHotelSearchUrl = ({
    kind,
    payload,
    destinationName,
    checkIn,
    checkOut,
    nights,
    roomConfigs,
    locale
}: BuildHotelSearchUrlInput): string => {
    const rooms = roomConfigs.length
    const adults = roomConfigs.reduce((sum, r) => sum + r.adults, 0)
    const children = roomConfigs.reduce((sum, r) => sum + r.children, 0)

    const params = new URLSearchParams()

    const isCity = kind === 'city'
    const cityId = payload.cityId || ''
    const predictiveId = isCity ? cityId : payload.uniqueId || ''
    const cityName = payload.cityName || destinationName
    const lat = payload.latitude || ''
    const long = payload.longitude || ''

    params.set('hotelSearchType', isCity ? 'City' : 'Hotel')
    params.set('checkInDate', getFormattedDate(checkIn, 'YYYY-MM-DD'))
    params.set('checkOutDate', getFormattedDate(checkOut, 'YYYY-MM-DD'))
    params.set('nights', String(nights))
    params.set('noOfRooms', String(rooms))
    params.set('noOfAdults', String(adults))
    params.set('noOfChildren', String(children))
    params.set('childrenAges', '')
    params.set('provinceName', payload.provinceName || '')
    params.set('criteria', '')
    params.set('predictiveId', predictiveId)
    params.set('starType', '')
    params.set('checkBreakfastSelected', 'false')
    params.set('resultsViewType', 'List')
    params.set('lng', locale)
    params.set('hotelClass', '')
    params.set('searchFrom', 'Predictive')
    params.set('countryName', payload.countryName || '')
    params.set('lat', lat)
    params.set('long', long)
    params.set('countryId', payload.countryId || '')

    if (isCity) {
        params.set('cityId', cityId)
        params.set('cityName', cityName)
    } else {
        params.set('cityId', cityId)
        params.set('cityName', cityName)
        if (payload.hotelName) {
            params.set('hotelName', payload.hotelName)
        }
    }

    return `/hotel/searchHotels?${params.toString()}`
}
